import type { HmlLoginDto, HmlLoginModel, HmlSaintIntegrateDto, SnsProvider } from '~/types/hml-type'
import { jwtDecode } from 'jwt-decode'
import { COOKIE_ACCESS_TOKEN_KEY } from '~/constants/cookieKeys'
import { STORAGE_RECENTLY_LOGIN_SNS_KEY } from '~/constants/storageKeys'

interface HMLJwtPayload {
  userId: number
  nickname: string
  profileImage: {
    id?: number
    url?: string
  }
  isWatvUser: boolean
  isTempNickname: boolean
}

export interface HmlSaintInfo {
  churchNm: string
  countryCd: string
  saintNm: string
}

export default function () {
  const { $api, $t } = useNuxtApp()
  const runtimeConfig = useRuntimeConfig()
  const { isWebview } = useWebviewBridge()

  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  const after30Day = new Date(today)
  after30Day.setDate(today.getDate() + 30)

  const jwtCookie = computed<string | null>({
    get() {
      return useCookie<string | null>(COOKIE_ACCESS_TOKEN_KEY).value
    },
    set(token) {
      useCookie<string | null>(COOKIE_ACCESS_TOKEN_KEY, {
        expires: getExpiresTime(),
      }).value = token
    },
  })

  function getExpiresTime() {
    return isWebview.value ? after30Day : tomorrow
  }

  const isLoggedIn = computed(() => {
    return !!jwtCookie.value
  })

  const userNickName = computed(() => {
    if (jwtCookie.value) {
      const payload = jwtDecode(jwtCookie.value)

      if (payload) {
        return (payload as HMLJwtPayload).nickname
      }
    }

    return ''
  })

  const recentlyLoginSns = useLocalStorage<SnsProvider | null>(STORAGE_RECENTLY_LOGIN_SNS_KEY, null)

  const isWatvUser = computed(() => {
    if (jwtCookie.value) {
      const payload = jwtDecode(jwtCookie.value)

      if (payload) {
        return (payload as HMLJwtPayload).isWatvUser
      }
    }

    return false
  })

  const userId = computed<number | null>(() => {
    if (jwtCookie.value) {
      const payload = jwtDecode(jwtCookie.value)

      if (payload) {
        return (payload as HMLJwtPayload).userId
      }
    }

    return null
  })

  const isTempNickname = computed(() => {
    if (jwtCookie.value) {
      const payload = jwtDecode(jwtCookie.value)

      if (payload) {
        return (payload as HMLJwtPayload).isTempNickname
      }
    }

    return false
  })

  const profileImage = computed(() => {
    if (jwtCookie.value) {
      const payload = jwtDecode(jwtCookie.value)

      if (payload) {
        return {
          url:
            (payload as HMLJwtPayload).profileImage.url
            ?? 'https://ws2.watv.org/file/download/8b5dd233-9dcd-11ef-97ec-001b21bc2241.jpg',
          id: (payload as HMLJwtPayload).profileImage.id,
        }
      }
    }

    return {
      url: 'https://ws2.watv.org/file/download/8b5dd233-9dcd-11ef-97ec-001b21bc2241.jpg',
      id: 0,
    }
  })

  function logout() {
    jwtCookie.value = null
    location.reload()
  }

  async function withdraw(params: { withdrawGb: number, reason?: string }) {
    const data = await $api<boolean>('/hml/v1/me/withdraw', {
      method: 'DELETE',
      body: {
        withdrawGb: params.withdrawGb,
        reason: params.reason,
      },
    })

    if (data) {
      logout()
    }
  }

  const isShowLoginPopup = useState('showLoginPopup', () => false)

  function showLoginPopup() {
    isShowLoginPopup.value = true
  }

  function hideLoginPopup() {
    isShowLoginPopup.value = false
  }

  async function changeMyNickname(nickname: string) {
    const data = await $api<string>('/hml/v1/me/nickname', {
      method: 'put',
      body: { nickname },
      onResponseError: (error) => {
        if (error.response.status === 409) {
          alert($t('이미사용중인닉네임이에요'))
        }
      },
    })

    if (data) {
      jwtCookie.value = data
    }
  }

  function loginWithKakao() {
    if (isWebview.value) {
      useWebviewBridge().loginWithKakao()
      return
    }

    const kakao = (window as any).Kakao
    kakao.init(runtimeConfig.public.kakaoJavascriptKey) //

    kakao.Auth.authorize({
      redirectUri: `${runtimeConfig.public.appDomain}/auth/kakao-callback`,
    })
  }

  function loginWithGoogle() {
    if (isWebview.value) {
      // 1.0.8 버전 앱부터 __hmlNativeGoogleLogin 신호를 주입함. 없으면 구앱 → 업데이트 안내.
      if ((window as any).__hmlNativeGoogleLogin) {
        useWebviewBridge().loginWithGoogle()
      }
      else {
        useToast().showToast({
          message: '구글 로그인은 최신 버전 앱에서 가능합니다. 앱을 업데이트해 주세요.<br/>Google sign-in requires the latest app version. Please update the app.',
        })
      }
      return
    }

    const url
      = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${
        runtimeConfig.public.googleClientKey
      }&redirect_uri=`
      + `${runtimeConfig.public.appDomain}/auth/google-callback`
      + `&response_type=code`
      + `&scope=email profile`

    location.href = url
  }

  async function loginWithApple(): Promise<HmlLoginModel | null> {
    const appleID = (window as any).AppleID

    appleID.auth.init({
      clientId: 'org.watv.heavenlymothersloveWeb',
      scope: 'email',
      redirectURI: `${runtimeConfig.public.appDomain}/auth/apple-callback`,
      state: 'hml_login',
      usePopup: true,
    })

    const res = await appleID.auth.signIn()
    const idToken = res?.authorization?.id_token

    if (!idToken) {
      return null
    }
    const { data } = await useAPI<HmlLoginModel>(`/hml/v1/auth/apple/id-token/${idToken}`, {
      method: 'GET',
      query: {
        languageGbSn: 'ko',
      },
    })

    return data.value ?? null
  }

  function integrateWatv(dto: HmlLoginDto) {
    return useAPI<HmlLoginModel>('/hml/v1/me/integrate/watv', {
      method: 'POST',
      body: dto,
    })
  }

  function integrateSaint(dto: HmlSaintIntegrateDto) {
    return useAPI<HmlLoginModel>('/hml/v1/me/integrate/saint', {
      method: 'POST',
      body: dto,
    })
  }

  async function refreshToken() {
    const { $api } = useNuxtApp()
    if (!jwtCookie.value) {
      return null
    }
    const data = await $api<string>('/hml/v1/me/token/refresh', {
      method: 'POST',
    })
    if (data) {
      jwtCookie.value = data
    }
  }

  async function getSaintInfo(languageGbSn: string) {
    return await useAPI<HmlSaintInfo | null>('/hml/v1/me/saint-info', {
      method: 'GET',
      query: {
        languageGbSn,
      },
    })
  }

  return {
    userNickName,
    jwtCookie,
    logout,
    withdraw,
    isShowLoginPopup,
    showLoginPopup,
    hideLoginPopup,
    isLoggedIn,
    changeMyNickname,
    profileImage,
    loginWithGoogle,
    loginWithKakao,
    integrateWatv,
    isWatvUser,
    integrateSaint,
    loginWithApple,
    recentlyLoginSns,
    isTempNickname,
    userId,
    refreshToken,
    getSaintInfo,
  }
}
